"use client"

import { useRouter } from "next/navigation"
import { useRef, useState } from "react"

import { listAllProducts } from "@lib/data/products"
import DesktopFloatingActions from "@modules/immersive-shop/components/desktop-floating-actions"
import ProductSearchOverlay from "@modules/immersive-shop/components/product-search-overlay"
import { adaptImmersiveShopData } from "@modules/immersive-shop/lib/product-adapter"
import styles from "@modules/immersive-shop/styles/immersive-shop.module.css"
import { ImmersiveShopProduct } from "@modules/immersive-shop/types"
import { SEARCH_CATALOG_FIELDS } from "@modules/products/templates/product-desktop-floating-actions-wrapper/catalog-fields"

type ProductDesktopFloatingActionsProps = {
  cartQuantity: number
  countryCode: string
  products: ImmersiveShopProduct[]
  regionId: string
}

export default function ProductDesktopFloatingActions({
  cartQuantity,
  countryCode,
  products: initialProducts,
  regionId,
}: ProductDesktopFloatingActionsProps) {
  const router = useRouter()
  const [searchOpen, setSearchOpen] = useState(false)
  const [products, setProducts] = useState(initialProducts)
  const catalogRequestRef = useRef<Promise<void> | null>(null)

  const ensureCatalog = () => {
    if (products.length > 0 || catalogRequestRef.current) {
      return
    }

    catalogRequestRef.current = listAllProducts({
      regionId,
      queryParams: {
        fields: SEARCH_CATALOG_FIELDS,
      },
    })
      .then((response) => {
        setProducts(adaptImmersiveShopData(response.products, []).products)
      })
      .catch(() => {})
      .finally(() => {
        catalogRequestRef.current = null
      })
  }

  const openProduct = (product: ImmersiveShopProduct) => {
    setSearchOpen(false)
    router.push(`/${countryCode}/products/${product.handle}`)
  }

  return (
    <div className={styles.productPageFloatingActionsScope}>
      <DesktopFloatingActions
        cartQuantity={cartQuantity}
        countryCode={countryCode}
        onSearchOpen={() => {
          ensureCatalog()
          setSearchOpen(true)
        }}
      />

      {searchOpen && (
        <ProductSearchOverlay
          onClose={() => setSearchOpen(false)}
          onSelectProduct={openProduct}
          onSubmitSearch={(_, matchingProducts) =>
            openProduct(matchingProducts[0])
          }
          products={products}
          scrollToTopOnSelect
        />
      )}
    </div>
  )
}
